/**
 * Estimation de l'effectif d'un établissement.
 *
 * Fonctions pures : tout arrive en argument, rien n'est lu ailleurs. Elles se
 * testent donc sans DOM, sans navigateur et sans Supabase (voir
 * tests/estimate.test.js).
 */

import { DEFAULT_RATIO_PRO } from './constants.js'

/** Un ratio absent, nul ou négatif retombe sur la valeur par défaut plutôt que de diviser par zéro. */
export function resolveRatio(ratioPro) {
  const r = Number.parseFloat(ratioPro)
  return Number.isFinite(r) && r > 0 ? r : DEFAULT_RATIO_PRO
}

/** true dès qu'un effectif a été saisi à la main — y compris 0, qui veut dire « aucun », pas « je ne sais pas ». */
export function hasManualPros(record) {
  return record != null && record.pros != null && record.pros !== ''
}

/**
 * Effectif estimé à partir de la clientèle inscrite. 0 si l'estimation ne
 * s'applique pas.
 *
 * Réservé aux GMF réguliers : la RAMQ ne publie d'inscriptions que pour eux, et
 * l'effectif d'un GMF-U est gonflé par les résidents, ce que le ratio ne capte
 * pas. Les hôpitaux n'ont pas de clientèle inscrite du tout.
 */
export function estimatePros(establishment, ratioPro) {
  if (!establishment || establishment.kind !== 'gmf' || establishment.type !== 'GMF') return 0
  const inscrits = establishment.inscrits || 0
  if (inscrits <= 0) return 0
  // Plancher à 1 : un GMF qui a des patients inscrits a au moins un professionnel.
  return Math.max(1, Math.round(inscrits / resolveRatio(ratioPro)))
}

/** Effectif retenu pour les calculs : la saisie manuelle prime toujours sur l'estimation. */
export function usersOf(establishment, record, ratioPro) {
  if (hasManualPros(record)) return Number.parseFloat(record.pros) || 0
  return estimatePros(establishment, ratioPro)
}

/** true quand le chiffre affiché vient de l'estimation — l'UI doit alors le dire. */
export function isEstimate(establishment, record, ratioPro) {
  return !hasManualPros(record) && estimatePros(establishment, ratioPro) > 0
}
